import { observer } from "mobx-react";
import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
    GoogleLoginResponse,
    GoogleLoginResponseOffline,
} from "react-google-login";

import { OAuthProviderClient } from "@formant/core/client/OAuthProviderClient";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IOAuthAuthorizeQueryParams } from "@formant/core/model/IOAuthAuthorizeQueryParams";
import { IValidation } from "@formant/core/model/IValidation";
import { Button } from "@formant/ui-kit";
import { InputElement } from "@formant/ui/component/common/Field";
import { Typography } from "@formant/ui/component/common/Typography";

import { adminApi } from "../../config";
import { useRouter } from "../../context/RouterContext";
import { useStores } from "../../context/StoreContext";
import { Checkbox } from "../common/Checkbox";
import { Form } from "../common/Form";
import { Modal } from "../common/Modal";
import { TextButton } from "../common/TextButton";
import { ValidationMessages } from "../common/ValidationMessages";
import { GoogleButton } from "./GoogleButton";
import {
    Bottom,
    ButtonContainer,
    HrLine,
    HrText,
    HrWithText,
    LoginContainer,
    Option,
    OptionLabel,
    PasswordFieldContainer,
    StyledFooter,
    TextFieldContainer,
} from "./Login.styles";

export const Login = observer(function Login() {
    const { routeTo } = useRouter();
    const { authStore } = useStores();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [persistentSession, setPersistentSession] = useState(
        authStore.persistentSession,
    );
    const [loading, setLoading] = useState(false);
    const [validation, setValidation] = useState<IValidation | undefined>(
        undefined,
    );

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        // Support OAuth clients linking directly to the login page with full OAuth params.
        // Only follow the OAuth flow when client_id, redirect_uri, and state are all present,
        // so platform-app / auth-lib flows that use only redirectUrl (no state) continue to work.
        const hasFullOauthParams =
            urlParams.has("client_id") &&
            urlParams.has("redirect_uri") &&
            urlParams.has("state");
        const alreadyInRedirectFlow = urlParams.has("redirectUrl");
        if (hasFullOauthParams && !alreadyInRedirectFlow) {
            const responseType = urlParams.get("response_type") ?? "";
            const clientId = urlParams.get("client_id") ?? "";
            const redirectUri = urlParams.get("redirect_uri") ?? "";
            const state = urlParams.get("state") ?? "";
            const codeChallenge = urlParams.get("code_challenge") ?? undefined;
            const codeChallengeMethod = (urlParams.get(
                "code_challenge_method",
            ) ??
                undefined) as IOAuthAuthorizeQueryParams["code_challenge_method"];

            const errors: string[] = [];
            if (responseType !== "code") {
                errors.push("response_type must be 'code'");
            }
            if (!clientId) {
                errors.push("client_id is required");
            }
            if (!redirectUri) {
                errors.push("redirect_uri is required");
            } else {
                try {
                    // Basic URL validation; admin-api will enforce registration match.
                    new URL(redirectUri);
                } catch {
                    errors.push("redirect_uri must be a valid URL");
                }
            }
            if (!state) {
                errors.push("state is required");
            }
            if (
                codeChallengeMethod !== undefined &&
                codeChallengeMethod !== "S256" &&
                codeChallengeMethod !== "plain"
            ) {
                errors.push("code_challenge_method must be 'S256' or 'plain'");
            }

            if (errors.length > 0) {
                setValidation({ login: errors });
            } else {
                const client = new OAuthProviderClient(adminApi);
                const authorizeUrl = client.authorizeUrl({
                    response_type: responseType,
                    client_id: clientId,
                    redirect_uri: redirectUri,
                    state,
                    code_challenge: codeChallenge,
                    code_challenge_method: codeChallengeMethod,
                });
                window.location.href = authorizeUrl;
            }
            return;
        }

        const loginError = urlParams.get("loginError");
        if (!loginError) {
            return;
        }

        if (loginError === "invalidRedirectUrl") {
            setValidation({
                login: [
                    "Invalid redirect URL provided. Please try again or contact support.",
                ],
            });
        } else if (loginError === "invalidOauthRedirect") {
            setValidation({
                login: [
                    "Invalid OAuth redirect parameters. Please restart the OAuth flow from your client application.",
                ],
            });
        }

        urlParams.delete("loginError");
        const url = new URL(window.location.href);
        url.search = urlParams.toString();
        window.history.replaceState({}, "", url.toString());
    }, []);

    const onGoogleLoginSuccess = useCallback(
        (code: GoogleLoginResponse | GoogleLoginResponseOffline) => {
            const tokenId = (code as GoogleLoginResponse).tokenId;
            setLoading(true);
            if (tokenId !== undefined) {
                authStore.loginWithGoogleToken(tokenId, false).catch(_ => {
                    throw _;
                });
            }
        },
        [authStore],
    );

    const onChangeEmail = useCallback(
        (event: FormEvent<InputElement>) => setEmail(event.currentTarget.value),
        [],
    );

    const onChangePassword = useCallback(
        (event: FormEvent<InputElement>) =>
            setPassword(event.currentTarget.value),
        [],
    );

    const onChangeStaySignedIn = useCallback(() => {
        const newPersistentSession = !persistentSession;
        setPersistentSession(newPersistentSession);
        authStore.setPersistentSession(newPersistentSession);
    }, [authStore, persistentSession]);

    const onSubmit = useCallback(async () => {
        setValidation(undefined);
        try {
            await authStore.login(email, password);
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 422) {
                setValidation(responseError.validationErrors);
                return;
            }

            if (responseError.statusCode !== 401) {
                throw responseError;
            }

            setValidation({
                login: ["Incorrect email address or password"],
            });
        }
    }, [email, password]);

    return (
        <Modal
            large={true}
            footer={
                routeTo.ssoLogin && (
                    <StyledFooter>
                        <Bottom>
                            <TextButton
                                data-testid="return-button"
                                to={routeTo.ssoLogin()}
                            >
                                Sign in with SSO
                            </TextButton>
                        </Bottom>
                    </StyledFooter>
                )
            }
            footerStyles={{ width: "100%", height: "5.5rem" }}
        >
            <LoginContainer $topOffset="3rem">
                <Typography type="h2">Sign in</Typography>
                <>
                    <ButtonContainer $marginTop="1.4375rem">
                        <GoogleButton
                            onGoogleLoginSuccess={onGoogleLoginSuccess}
                        />
                    </ButtonContainer>
                    <HrWithText>
                        <HrLine />
                        <HrText>or</HrText>
                        <HrLine />
                    </HrWithText>
                </>
                <Form onSubmit={onSubmit} onLoading={setLoading}>
                    <TextFieldContainer
                        data-testid="email-text-input"
                        label="Email"
                        isEmail={true}
                        value={email}
                        onChange={onChangeEmail}
                        autoComplete="username"
                        small={true}
                        invalid={!!validation?.email}
                    />
                    <PasswordFieldContainer
                        data-testid="password-text-input"
                        label="Password"
                        value={password}
                        onChange={onChangePassword}
                        autoComplete="current-password"
                        small={true}
                        invalid={!!validation?.password}
                    />
                    <ValidationMessages validation={validation} />
                    <Option>
                        <div>
                            <OptionLabel>
                                <Checkbox
                                    data-testid="keep-me-logged-in-checkbox"
                                    checked={persistentSession}
                                    onChange={onChangeStaySignedIn}
                                />
                                <span>Stay signed in</span>
                            </OptionLabel>
                        </div>
                        {routeTo.forgotPassword && (
                            <TextButton
                                data-testid="forgot-password-button"
                                to={routeTo.forgotPassword()}
                            >
                                Forgot password
                            </TextButton>
                        )}
                    </Option>
                    <ButtonContainer $marginTop="1.4375rem">
                        <Button
                            className="text-base"
                            data-testid="login-button"
                            size="lg"
                            loading={loading}
                        >
                            Sign In
                        </Button>
                    </ButtonContainer>
                </Form>
            </LoginContainer>
        </Modal>
    );
});
